import { View } from '@/components/ui/tw-wrappers';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet } from 'react-native';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';

import { CountrySelect } from '@/components/country-select';
import { MultiCountrySelect } from '@/components/multi-country-select';
import { Text } from '@/components/text';
import { Card } from '@/components/ui/card';
import { NumberInput } from '@/components/ui/number-input';
import { Select } from '@/components/ui/select';
import { CONCEPTS } from '@/constants/scenarios';
import { useRestrictions } from '@/hooks/use-restrictions';
import { useScenarioStore } from '@/stores/scenario-store';
import { Country } from '@/types/country';
import { getOnboardingSummarySchema, type ScenarioFormValues } from '@/types/scenario.schema';
import { useFormatters } from '@/utils/formatters/useFormatters';
import { IconSymbol } from './ui/icon-symbol';

import { Colors } from '@/constants/theme';

export function OnboardingSummary({
  countries,
  countriesLoading,
  exchangeRate,
}: {
  countries: Country[];
  countriesLoading?: boolean;
  exchangeRate?: number;
}) {
  const { t } = useTranslation();
  const inputs = useScenarioStore(s => s.inputs);
  const setInputs = useScenarioStore(s => s.setInputs);
  const { blockedSet } = useRestrictions();
  const { formatNumber, formatCurrency } = useFormatters();

  const blockedIds = useMemo(() => Array.from(blockedSet), [blockedSet]);

  // Use a scoped schema managed centrally
  const schema = useMemo(() => getOnboardingSummarySchema(t), [t]);

  // Split inputs to isolate those ONLY managed by this summary form
  const { partnerMargin: _m, sellThroughLocalPartner: _s, selectedScenarios: _ss, ...summaryRelevantInputs } = inputs;

  const { control, formState, watch, setValue, clearErrors, reset } = useForm<ScenarioFormValues>({
    resolver: zodResolver(schema as any),
    defaultValues: summaryRelevantInputs as any,
    mode: 'onChange',
  });

  const { errors, isValid } = formState;
  const watchedValues = watch();

  const lastPatchedState = React.useRef<string | null>(null);

  // Sync form changes with store automatically
  React.useEffect(() => {
    if (isValid) {
      const keysToSync: (keyof typeof summaryRelevantInputs)[] = [
        'vendorIdList',
        'customerId',
        'vendorId',
        'invoicingConcept',
        'invoicingAmount',
      ];

      const storeState = keysToSync.reduce((acc, k) => {
        acc[k] = summaryRelevantInputs[k];
        return acc;
      }, {} as any);

      const formStateObj = keysToSync.reduce((acc, k) => {
        acc[k] = watchedValues[k];
        return acc;
      }, {} as any);

      const storeString = JSON.stringify(storeState);
      const formString = JSON.stringify(formStateObj);

      if (storeString === formString) {
        return; // States match perfectly, no patch needed
      }

      if (lastPatchedState.current === formString) {
        return; // We already emitted this exact form state to Zustand! Stop the loop.
      }

      if (blockedSet.has(watchedValues.vendorId as string)) {
        return;
      }

      lastPatchedState.current = formString;
      setInputs(formStateObj);
    }
  }, [watchedValues, isValid, blockedSet, setInputs, summaryRelevantInputs]);

  const vendorIdList = (watchedValues.vendorIdList || []) as string[];
  const customerId = (watchedValues.customerId || '') as string;
  const vendorId = (watchedValues.vendorId || '') as string;

  // For display in summary mode
  const customer = countries.find(c => c.id === inputs.customerId);

  // For display in edit mode (country objects)
  const currentCustomer = countries.find(c => c.id === customerId);
  const localInvoicingAmount = (watchedValues?.invoicingAmount || 0) * (exchangeRate || 1);

  const onClear = () => {
    useScenarioStore.getState().resetInputs();
    useScenarioStore.getState().setOnboardingOpen(true);
    reset(useScenarioStore.getState().inputs as any);
  };

  return (
    <Animated.View entering={FadeIn.delay(50)} exiting={FadeOut} style={styles.container}>
      <Card backgroundColor='white'>
        <View className='flex-col items-stretch gap-3 mb-4 pb-5 border-b border-black/6 z-10 md:flex-row md:justify-between md:items-center md:gap-4'>
          <View className='flex-row items-center justify-between md:gap-3 min-h-[39px] flex-1'>
            <Text type='subtitle' style={styles.titleText}>
              {t('onboarding.summary.title')}
            </Text>
            <View style={styles.actions}>
              <Pressable onPress={onClear} hitSlop={20} style={styles.iconPressable}>
                <IconSymbol name='arrow.clockwise' color='gray' />
                <Text style={styles.iconLabel}>{t('common.reset')}</Text>
              </Pressable>
            </View>
          </View>
        </View>

        {/* grid */}
        <View style={styles.grid}>
          {/* 1. Vendor list */}
          <View style={styles.item}>
            <Text style={[styles.label, { color: Colors.light.primary, opacity: 1 }]}>
              {t('onboarding.summary.vendorCountryList')}
            </Text>

            <View style={styles.editWrap}>
              <Controller
                control={control}
                name='vendorIdList'
                render={({ field: { value, onChange } }) => (
                  <MultiCountrySelect
                    label={''}
                    // tooltip={t('onboarding.fields.vendorCountryListTooltip')}
                    countries={countries}
                    valueIds={value || []}
                    disabled={countriesLoading}
                    excludeIds={customerId ? [customerId] : []}
                    onChange={ids => {
                      onChange(ids);
                      if (ids.length > 0) clearErrors('vendorIdList');

                      // Auto logic for the single selection
                      if (ids.length === 1) {
                        setValue('vendorId', ids[0], { shouldValidate: true });
                      } else if (ids.length === 0) {
                        setValue('vendorId', '', { shouldValidate: true });
                      } else {
                        // If multiple, ensure current vendorId is in the list
                        if (vendorId && !ids.includes(vendorId)) {
                          setValue('vendorId', '', { shouldValidate: true });
                        }
                      }
                    }}
                  />
                )}
              />
              {errors.vendorIdList && <Text style={styles.errorText}>{errors.vendorIdList.message}</Text>}
            </View>
          </View>

          {/* 2. Vendor  - Revenue Recognition Country */}
          <View style={styles.item}>
            <Text style={[styles.label, { color: Colors.light.primary, opacity: 1 }]}>
              {t('onboarding.summary.vendorDestination')}
            </Text>

            <View style={styles.editWrap}>
              <Controller
                control={control}
                name='vendorId'
                render={({ field: { value, onChange } }) => (
                  <CountrySelect
                    label={''} // Hide label
                    countries={countries.filter(c => vendorIdList?.includes(c.id))}
                    valueId={value || null}
                    disabled={countriesLoading}
                    hideClearButton
                    excludeIds={customerId ? [customerId] : []}
                    // excludeIds={vendorIdList}
                    //disabledIds={blockedIds}
                    placeholder={t('onboarding.fields.searchPlaceholder', 'Search destination…')}
                    onChange={c => {
                      const id = c?.id ?? '';
                      onChange(id);
                      if (id) clearErrors('vendorId');
                    }}
                  />
                )}
              />
              {errors.vendorId && <Text style={styles.errorText}>{errors.vendorId.message}</Text>}
            </View>
          </View>

          {/* 3. Customer (Origin)  - Expansion Country */}
          <View style={styles.item}>
            <Text style={[styles.label, { color: Colors.light.primary, opacity: 1 }]}>
              {t('onboarding.summary.customerOrigin')}
            </Text>

            <View style={styles.editWrap}>
              <Controller
                control={control}
                name='customerId'
                render={({ field: { value, onChange } }) => (
                  <CountrySelect
                    label={''} // Hide label since we render it above
                    countries={countries}
                    valueId={value || null}
                    hideClearButton
                    disabledIds={blockedIds}
                    disabled={countriesLoading}
                    excludeIds={vendorIdList}
                    onChange={c => {
                      const id = c?.id ?? '';
                      onChange(id);
                      if (id) clearErrors('customerId');
                      if (vendorId === id) {
                        setValue('vendorId', '', { shouldValidate: true });
                      }
                    }}
                  />
                )}
              />
              {errors.customerId && <Text style={styles.errorText}>{errors.customerId.message}</Text>}
            </View>
          </View>
          {/* 4. Concept */}
          <View style={styles.item}>
            <Text style={[styles.label, { color: Colors.light.primary, opacity: 1 }]}>
              {t('onboarding.summary.invoicingConcept')}
            </Text>

            <View style={styles.selectWrap}>
              <Controller
                control={control}
                name='invoicingConcept'
                render={({ field: { value, onChange } }) => (
                  <Select
                    value={value}
                    options={CONCEPTS.map(c => ({
                      value: c.key,
                      label: t(`${c.i18nKey}.label`, c.key),
                    }))}
                    onValueChange={onChange}
                  />
                )}
              />
            </View>
          </View>

          {/* 5. Amount & Currency */}
          <View style={styles.item}>
            <Text style={[styles.label, { color: Colors.light.primary, opacity: 1 }]}>
              {t('onboarding.summary.invoicingAmount')}
            </Text>

            <View style={styles.editWrap}>
              <View style={styles.amountCurrencyRow}>
                <View style={styles.amountInputWrap}>
                  <Controller
                    control={control}
                    name='invoicingAmount'
                    render={({ field: { value, onChange } }) => (
                      <NumberInput value={value} placeholder='10000' onChange={onChange} prefix='USD' />
                    )}
                  />
                </View>
              </View>
              {errors.invoicingAmount && <Text style={styles.errorText}>{errors.invoicingAmount.message}</Text>}
            </View>
          </View>

          {/* 6. rate */}
          <View style={styles.item}>
            <Text style={[styles.label, { color: Colors.light.primary, opacity: 1 }]}>
              {t('scenarios.card.exchangeRate')}
            </Text>
            <View className='min-h-12 justify-center mt-2'>
              <Text type='default' className='mt-2' style={styles.value}>
                USD/{customer?.currencyISO}: {exchangeRate != null ? formatNumber(exchangeRate, 2) : '-'}
              </Text>
            </View>
          </View>

          {/* 7. invoicing in local currency */}
          <View style={styles.item}>
            <Text style={[styles.label, { color: Colors.light.primary, opacity: 1 }]}>
              {t('scenarios.card.invoiceAmountLocal')}
            </Text>
            <View className='min-h-12 justify-center mt-2'>
              <Text type='default' className='mt-2' style={styles.value}>
                {exchangeRate != null
                  ? formatCurrency(localInvoicingAmount, currentCustomer?.currencyISO || 'USD')
                  : '-'}
              </Text>
            </View>
          </View>
        </View>
        <View style={styles.footer} />
      </Card>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    alignSelf: 'center',
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 24,
    alignContent: 'flex-start',
    position: 'relative',
    zIndex: 1,
  },
  item: {
    flexBasis: 280,
    minWidth: 240,
    maxWidth: 320,
    gap: 4,
    position: 'relative',
  },
  editWrap: {
    flex: 1,
  },
  selectWrap: {
    flex: 1,
    marginTop: 8,
  },
  label: {
    fontSize: 14,
    opacity: 0.5,
    color: '#666',
    marginBottom: 4,
    height: 24,
  },
  value: {
    fontSize: 14,
    minHeight: 30,
    textAlignVertical: 'center',
  },
  titleText: {
    flexShrink: 1,
  },
  iconPressable: {
    padding: 5,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  iconLabel: {
    color: Colors.light.secondaryText,
    fontSize: 11,
  },
  amountCurrencyRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  amountInputWrap: {
    flex: 2,
  },
  capitalize: {
    textTransform: 'capitalize',
  },
  errorText: {
    color: '#CB2F39',
    fontSize: 11,
    marginTop: 2,
  },
  chipsContainer: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    alignItems: 'center',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    alignItems: 'center',
    marginTop: 0,
  },
});
